import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Award, User, GraduationCap } from "lucide-react";
import { useSiteContent } from "@/hooks/useSiteContent";
import { DEFAULTS } from "@/lib/content_defaults";

export default function Coaches() {
  const [coaches, setCoaches] = useState([]);
  const [signees, setSignees] = useState([]);
  const { data: hero } = useSiteContent("page_hero_coaches", DEFAULTS.page_hero_coaches);
  useEffect(() => {
    api.get("/coaches").then(r => setCoaches(r.data));
    api.get("/signees").then(r => setSignees(r.data));
  }, []);

  return (
    <div>
      <section className="section-dark py-20">
        <div className="container-app">
          <div className="label-tag mb-4">{hero.label_tag}</div>
          <h1 className="font-heading font-black uppercase text-5xl md:text-7xl leading-none">
            {hero.line1}<br/><span className="metallic-gold">{hero.line2}</span><br/>{hero.line3}
          </h1>
          <p className="mt-6 text-white/70 max-w-2xl text-lg">{hero.subtitle}</p>
        </div>
      </section>
      <section className="section-light py-16">
        <div className="container-app grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="coaches-grid">
          {coaches.map((c) => {
            const cSignees = signees.filter(s => s.coach_id === c.id);
            return (
              <div key={c.id} className="card-light hover-lift overflow-hidden flex flex-col" data-testid={`coach-card-${c.id}`}>
                <div className="aspect-[4/5] bg-neutral-100 overflow-hidden relative">
                  {c.photo_url ? <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover object-top"/> : <div className="w-full h-full flex items-center justify-center"><User size={64} className="text-neutral-300"/></div>}
                  <div className="absolute top-3 left-3 bg-black text-[color:var(--gold)] font-heading uppercase text-[10px] tracking-widest px-3 py-1 border border-[color:var(--gold)]">
                    10+ Yrs Coaching
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="label-tag mb-2" style={{ color: "#000" }}>{c.title}</div>
                  <div className="font-heading font-black uppercase text-2xl">{c.name}</div>
                  <p className="mt-3 text-neutral-700 text-sm leading-relaxed">{c.bio}</p>
                  {c.experience && <div className="mt-4 text-xs text-neutral-500 uppercase tracking-widest">{c.experience}</div>}
                  {c.certifications?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {c.certifications.map((cert,i) => (
                        <span key={i} className="text-[10px] font-mono uppercase px-2 py-1 border border-black/15 flex items-center gap-1"><Award size={10}/> {cert}</span>
                      ))}
                    </div>
                  )}
                  {c.philosophy && (
                    <div className="mt-5 pt-5 border-t border-black/10">
                      <div className="label-tag mb-1" style={{ color: "#000" }}>Philosophy</div>
                      <p className="italic text-neutral-600 text-sm">&ldquo;{c.philosophy}&rdquo;</p>
                    </div>
                  )}
                  {cSignees.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-black/10">
                      <div className="label-tag mb-3 flex items-center gap-1" style={{ color: "#000" }}><GraduationCap size={12}/> Recent Signees</div>
                      <ul className="space-y-2" data-testid={`coach-signees-${c.id}`}>
                        {cSignees.slice(0, 6).map(s => (
                          <li key={s.id} className="flex items-center gap-3 text-sm">
                            {s.college_logo_url && <img src={s.college_logo_url} alt={s.college} className="w-6 h-6 object-contain flex-shrink-0"/>}
                            <div className="flex-1 min-w-0">
                              <div className="font-heading uppercase font-bold text-xs truncate">{s.player_name} {s.class_year && <span className="text-neutral-500 font-normal">· {s.class_year}</span>}</div>
                              <div className="text-neutral-500 text-xs truncate">
                                {s.position && `${s.position} · `}<span className="text-[color:var(--gold)] font-medium">{s.college}</span>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
